import {
  ChevronLeft, ChevronRight, Users, X, Save 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Menu } from '@/features/menus';
import {
  MONTHS, WEEKDAYS, getMenuTypeStyle, getUtilizationStyle 
} from './constants';
import {
  getMenusForDate, getPaxForDate, formatDateForDisplay 
} from './calendar-operations';

interface CalendarViewProps {
  readonly menus: Menu[];
  readonly currentMonth: Date;
  readonly setCurrentMonth: (date: Date) => void;
  readonly calendarDays: (Date | null)[];
  readonly forecastPax: Record<string, number>;
  readonly selectedDate: Date | null;
  readonly editingPax: Record<string, number>;
  readonly setEditingPax: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  readonly openDayModal: (date: Date) => void;
  readonly saveDayForecast: () => void;
  readonly setSelectedDate: (date: Date | null) => void;
}

export function CalendarView({
  menus,
  currentMonth,
  setCurrentMonth,
  calendarDays,
  forecastPax,
  selectedDate,
  editingPax,
  setEditingPax,
  openDayModal,
  saveDayForecast,
  setSelectedDate,
}: CalendarViewProps) {
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((day) => (
          <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
            {day}
          </div>
        ))}
        {calendarDays.map((day, idx) => {
          const menusForDay = day ? getMenusForDate(menus, day) : [];
          const hasMenus = menusForDay.length > 0;

          const getButtonClassName = (): string => {
            if (hasMenus) return 'cursor-pointer bg-background hover:bg-muted/50 hover:border-primary';
            if (day) return 'bg-background';
            return 'bg-muted/20';
          };

          return (
            <button
              type="button"
              key={day ? day.toISOString() : `empty-${idx.toString()}`}
              onClick={() => day && hasMenus && openDayModal(day)}
              disabled={!day || !hasMenus}
              className={`min-h-24 rounded-md border p-2 text-left transition-colors ${getButtonClassName()}`}
            >
              {day && (
                <>
                  <div className="text-sm font-medium">{day.getDate()}</div>
                  <div className="mt-1 space-y-1">
                    {menusForDay.slice(0, 3).map((menu) => (
                      <div
                        key={menu.menuId}
                        className={`truncate rounded px-1 py-0.5 text-xs ${getMenuTypeStyle(menu.type)}`}
                      >
                        {menu.name}
                      </div>
                    ))}
                    {menusForDay.length > 3 && (
                      <div className="text-xs text-muted-foreground">+{menusForDay.length - 3} more</div>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" />
                    {getPaxForDate(menus, day, forecastPax)}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Day Edit Modal */}
      {selectedDate && (
        <DayEditModal
          selectedDate={selectedDate}
          menus={menus}
          editingPax={editingPax}
          setEditingPax={setEditingPax}
          saveDayForecast={saveDayForecast}
          setSelectedDate={setSelectedDate}
        />
      )}
    </div>
  );
}

interface DayEditModalProps {
  readonly selectedDate: Date;
  readonly menus: Menu[];
  readonly editingPax: Record<string, number>;
  readonly setEditingPax: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  readonly saveDayForecast: () => void;
  readonly setSelectedDate: (date: Date | null) => void;
}

function DayEditModal({
  selectedDate,
  menus,
  editingPax,
  setEditingPax,
  saveDayForecast,
  setSelectedDate,
}: DayEditModalProps) {
  const menusForDay = getMenusForDate(menus, selectedDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">
              Edit Forecast - {formatDateForDisplay(selectedDate)}
            </h3>
            <p className="text-sm text-muted-foreground">Adjust expected pax for each menu</p>
          </div>
          <button type="button" onClick={() => setSelectedDate(null)} className="rounded-full p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {menusForDay.map((menu) => {
            const currentPax = editingPax[menu.menuId] ?? 0;
            const utilization = menu.venueCapacity ? Math.round((currentPax / menu.venueCapacity) * 100) : 0;

            return (
              <div key={menu.menuId} className="rounded-lg border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${getMenuTypeStyle(menu.type)}`}>
                        {menu.type}
                      </span>
                      <span className="font-medium">{menu.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{menu.venueName}</p>
                  </div>
                  {menu.venueCapacity && (
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">Capacity</p>
                      <p className="font-medium">{menu.venueCapacity.toLocaleString()}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm text-muted-foreground">Expected Pax</label>
                    <input
                      type="number"
                      value={currentPax}
                      onChange={(e) => setEditingPax((prev) => ({
                        ...prev,
                        [menu.menuId]: parseInt(e.target.value) || 0 
                      }))}
                      className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      min={0}
                      max={menu.venueCapacity ?? 9999}
                    />
                  </div>
                  <div className="w-24 text-center">
                    <p className="text-sm text-muted-foreground">Utilization</p>
                    <p className={`text-lg font-bold ${getUtilizationStyle(utilization)}`}>
                      {utilization}%
                    </p>
                  </div>
                </div>

                {utilization > 100 && (
                  <p className="mt-2 text-xs text-red-600">Warning: Exceeds venue capacity</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => setSelectedDate(null)}>
            Cancel
          </Button>
          <Button onClick={saveDayForecast}>
            <Save className="mr-2 h-4 w-4" />
            Save Forecast
          </Button>
        </div>
      </div>
    </div>
  );
}
